// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

interface PollDisplayProps {
  postId: string;
  pollQuestion: string;
  pollOptions: string[];
}

interface VoteCounts {
  [key: number]: number;
}

export const PollDisplay: React.FC<PollDisplayProps> = ({
  postId,
  pollQuestion,
  pollOptions,
}) => {
  const { user } = useAuth();
  const [userVote, setUserVote] = useState<number | null>(null);
  const [voteCounts, setVoteCounts] = useState<VoteCounts>({});
  const [totalVotes, setTotalVotes] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    fetchVotes();
  }, [postId]);

  const fetchVotes = async () => {
    // Fetch all votes for this post
    const { data: votes, error } = await supabase
      .from('wall_post_poll_votes')
      .select('option_index, profile_id')
      .eq('post_id', postId);

    if (error) {
      console.error('Error fetching votes:', error);
      return;
    }

    // Count votes per option
    const counts: VoteCounts = {};
    votes?.forEach((vote) => {
      counts[vote.option_index] = (counts[vote.option_index] || 0) + 1;
      if (user && vote.profile_id === user.id) {
        setUserVote(vote.option_index);
      }
    });

    setVoteCounts(counts);
    setTotalVotes(votes?.length || 0);
  };

  const handleVote = async (optionIndex: number) => {
    if (!user) {
      toast.error('Please sign in to vote');
      return;
    }

    setIsLoading(true);

    try {
      if (userVote === optionIndex) {
        // Remove vote
        const { error } = await supabase
          .from('wall_post_poll_votes')
          .delete()
          .eq('post_id', postId)
          .eq('profile_id', user.id);

        if (error) throw error;

        setUserVote(null);
        setVoteCounts((prev) => ({
          ...prev,
          [optionIndex]: Math.max(0, (prev[optionIndex] || 0) - 1),
        }));
        setTotalVotes((prev) => Math.max(0, prev - 1));
      } else {
        // Upsert vote
        const { error } = await supabase.from('wall_post_poll_votes').upsert(
          {
            post_id: postId,
            profile_id: user.id,
            option_index: optionIndex,
          },
          { onConflict: 'post_id,profile_id' }
        );

        if (error) throw error;

        // Update local state
        if (userVote !== null) {
          setVoteCounts((prev) => ({
            ...prev,
            [userVote]: Math.max(0, (prev[userVote] || 0) - 1),
            [optionIndex]: (prev[optionIndex] || 0) + 1,
          }));
        } else {
          setVoteCounts((prev) => ({
            ...prev,
            [optionIndex]: (prev[optionIndex] || 0) + 1,
          }));
          setTotalVotes((prev) => prev + 1);
        }
        setUserVote(optionIndex);
      }
    } catch (error) {
      console.error('Error voting:', error);
      toast.error('Failed to submit vote');
    } finally {
      setIsLoading(false);
    }
  };

  const getPercentage = (index: number) => {
    if (totalVotes === 0) return 0;
    return Math.round(((voteCounts[index] || 0) / totalVotes) * 100);
  };

  const hasVoted = userVote !== null;

  return (
    <div className="mt-4 p-4 bg-muted/30 rounded-lg border border-border">
      <p className="font-semibold mb-3 flex items-center gap-2">
        <span className="text-lg">🗳️</span>
        {pollQuestion}
      </p>

      <div className="space-y-2">
        {pollOptions.map((option, index) => (
          <Button
            key={index}
            variant={userVote === index ? 'default' : 'outline'}
            className={cn(
              'w-full justify-start relative overflow-hidden h-auto py-3',
              hasVoted && 'pointer-events-auto'
            )}
            onClick={() => handleVote(index)}
            disabled={isLoading}
          >
            {hasVoted && (
              <div
                className={cn(
                  'absolute left-0 top-0 bottom-0 transition-all duration-500',
                  userVote === index
                    ? 'bg-primary/20'
                    : 'bg-muted'
                )}
                style={{ width: `${getPercentage(index)}%` }}
              />
            )}
            <span className="relative z-10 flex-1 text-left">{option}</span>
            {hasVoted && (
              <span className="relative z-10 font-bold ml-2">
                {getPercentage(index)}%
              </span>
            )}
          </Button>
        ))}
      </div>

      <p className="text-xs text-muted-foreground mt-3 text-center">
        {totalVotes} {totalVotes === 1 ? 'vote' : 'votes'}
        {hasVoted && ' • Click to change your vote'}
      </p>
    </div>
  );
};
